/**
 * Gear List Page
 * 
 * Displays all gear (toys, equipment, accessories) with search and filtering.
 * Layout matches other list pages (Archetypes, Sex Actions).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Package, Zap, Lock, Target, Shirt, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';
import { Spinner } from '../components/Animations';
import PopularityBadge from '../components/PopularityBadge';
import DatabaseHero from '../components/DatabaseHero';

// Price range color
const getPriceColor = (price) => {
    if (!price) return 'text-dark-500';
    const count = (price.match(/\$/g) || []).length;
    if (count <= 1) return 'text-emerald-400';
    if (count <= 2) return 'text-amber-400';
    if (count <= 3) return 'text-orange-400';
    return 'text-rose-400';
};

// Intensity bar component
const IntensityBar = ({ level }) => {
    const normalized = Math.min(10, Math.max(1, level || 5));
    return (
        <div className="flex items-center gap-1" title={`Intensity: ${normalized}/10`}>
            {[...Array(5)].map((_, i) => {
                const filled = (i + 1) * 2 <= normalized;
                const half = (i + 1) * 2 - 1 === normalized;
                return (
                    <div
                        key={i}
                        className={`w-1.5 h-3 ${filled ? 'bg-wine-500' : half ? 'bg-wine-500/50' : 'bg-dark-700'
                            }`}
                    />
                );
            })}
        </div>
    );
};

// Image-centric Gear Card Component
const GearCard = ({ gear }) => {
    const priceColor = getPriceColor(gear.priceRange);
    const hasImage = gear.imageUrl;

    return (
        <Link
            to={`/gear/${gear.id}`}
            className="group block bg-[#0A0A0A]/80 border border-wine-800/30 hover:border-[#C4A35A]/30 transition-all duration-300 overflow-hidden"
        >
            {/* Image Area */}
            <div className="relative aspect-[4/3] bg-dark-950 overflow-hidden">
                {hasImage ? (
                    <img
                        src={gear.imageUrl}
                        alt={gear.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-dark-700">
                        {gear.isElectronic ? <Zap size={48} /> :
                            gear.isRestraint ? <Lock size={48} /> :
                                gear.isImpact ? <Target size={48} /> :
                                    gear.isWearable ? <Shirt size={48} /> :
                                        <Package size={48} />}
                    </div>
                )}

                {/* Price Badge */}
                {gear.priceRange && (
                    <div className={`absolute top-2 right-2 px-2 py-1 bg-dark-900/90 backdrop-blur-sm font-bold ${priceColor}`}>
                        {gear.priceRange}
                    </div>
                )}

                {/* Category Badge */}
                {gear.category && (
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-dark-900/90 backdrop-blur-sm text-xs text-dark-300">
                        {gear.category}
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-3">
                {/* Name & Intensity */}
                <div className="flex items-start justify-between gap-2">
                    <h3 className="font-fantasy font-semibold text-cream/90 group-hover:text-[#C4A35A] transition-colors line-clamp-1 tracking-wide">
                        {gear.name}
                    </h3>
                    <IntensityBar level={gear.intensity} />
                </div>

                {/* Summary */}
                {gear.summary && (
                    <p className="mt-1.5 text-xs text-dark-500 line-clamp-3">
                        {gear.summary}
                    </p>
                )}

                {/* Compact Flags */}
                <div className="flex flex-wrap gap-1 mt-2">
                    {gear.isElectronic && <span className="text-amber-400 text-xs">⚡</span>}
                    {gear.isRestraint && <span className="text-purple-400 text-xs">🔒</span>}
                    {gear.isImpact && <span className="text-rose-400 text-xs">🎯</span>}
                    {gear.isPenetrating && <span className="text-pink-400 text-xs">💫</span>}
                    {gear.isWearable && <span className="text-blue-400 text-xs">👔</span>}
                </div>

                {/* Popularity & Velocity */}
                <div className="mt-2 pt-2 border-t border-dark-800/50">
                    <PopularityBadge
                        popularity={gear.popularity}
                        velocity={gear.popularityVelocity}
                        compact
                    />
                </div>
            </div>
        </Link>
    );
};

const GearPage = () => {
    const [gear, setGear] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [expandedCategories, setExpandedCategories] = useState({});

    // Fetch gear on mount
    useEffect(() => {
        fetchGear();
    }, []);

    const fetchGear = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getGear();
            setGear(data.gear || []);
            setCategories(data.categories || []);
        } catch (err) {
            console.error('Error fetching gear:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Filter gear based on search and category
    const filteredGear = useMemo(() => {
        let result = gear;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(g =>
                g.name?.toLowerCase().includes(query) ||
                g.description?.toLowerCase().includes(query) ||
                g.category?.toLowerCase().includes(query)
            );
        }

        if (selectedCategory) {
            result = result.filter(g => g.category === selectedCategory);
        }

        return result;
    }, [gear, searchQuery, selectedCategory]);

    // Group by category for accordion view
    const gearByCategory = useMemo(() => {
        if (searchQuery.trim()) return null; // Don't group when searching

        const groups = {};
        filteredGear.forEach(g => {
            const cat = g.category || 'Uncategorized';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(g);
        });
        return groups;
    }, [filteredGear, searchQuery]);

    const toggleCategory = (cat) => {
        setExpandedCategories(prev => ({
            ...prev,
            [cat]: !prev[cat]
        }));
    };

    if (loading) {
        return (
            <div className="h-[calc(100vh-64px)] flex items-center justify-center bg-charcoal">
                <div className="text-center">
                    <Spinner size="lg" className="mx-auto mb-4" />
                    <p className="text-dark-400">Loading gear...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="h-[calc(100vh-64px)] flex items-center justify-center bg-charcoal">
                <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">⚠️</div>
                    <h2 className="text-xl font-bold text-white mb-2">Failed to Load Gear</h2>
                    <p className="text-dark-400 mb-4">{error}</p>
                    <button onClick={fetchGear} className="btn-primary">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden bg-charcoal">
            {/* Page Header with Search */}
            <div className="flex-shrink-0 border-b border-wine-900/20 px-6 py-4">
                <div className="max-w-[1800px] mx-auto">
                    <div className="flex items-center gap-4">
                        {/* Search */}
                        <div className="flex-1">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search gear by name, description, or category..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="input pl-12 pr-12"
                                />
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-500">
                                    <Search className="w-5 h-5" />
                                </div>
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-500 hover:text-dark-300"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Category Filter */}
                        {categories.length > 0 && (
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="input w-48"
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto w-full">
                <main className="max-w-[1800px] w-full mx-auto px-6 pt-4 pb-12">
                    {/* Hero Section */}
                    {!searchQuery.trim() && (
                        <DatabaseHero
                            icon={Package}
                            label="Equipment"
                            title="Gear & Equipment"
                            highlightWord="Equipment"
                            description="Explore toys, restraints, accessories, and equipment."
                            stats={[
                                { value: gear.length, label: 'Items' },
                                { value: categories.length, label: 'Categories' }
                            ]}
                            loading={loading}
                        />
                    )}

                    {/* Search Results */}
                    {searchQuery.trim() && (
                        <div className="mb-6">
                            <p className="text-sm text-dark-500 mb-3">
                                {filteredGear.length} result{filteredGear.length !== 1 ? 's' : ''} for "{searchQuery}"
                            </p>
                            {filteredGear.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {filteredGear.map(g => (
                                        <GearCard key={g.id} gear={g} />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-dark-500">
                                    No gear found matching your search.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Accordion Groups - hide when searching */}
                    {!searchQuery.trim() && gearByCategory && (
                        <div className="space-y-3">
                            {Object.entries(gearByCategory).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => {
                                const isExpanded = expandedCategories[category] !== false; // Default open

                                return (
                                    <div key={category} className="border border-wine-800/30 hover:border-wine-700/40 rounded-xl overflow-hidden transition-all duration-300">
                                        {/* Category Header */}
                                        <button
                                            onClick={() => toggleCategory(category)}
                                            className="w-full flex items-center justify-between px-5 py-4 bg-[#0A0A0A]/80 hover:bg-wine-950/30 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-lg font-fantasy font-semibold text-cream/90 tracking-wide">{category}</span>
                                                <span className="text-sm text-cream/40">({items.length})</span>
                                            </div>
                                            {isExpanded ? (
                                                <ChevronUp className="w-5 h-5 text-cream/40" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-cream/40" />
                                            )}
                                        </button>

                                        {/* Category Content */}
                                        {isExpanded && (
                                            <div className="p-4 bg-dark-950/30">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                    {items.map(g => (
                                                        <GearCard key={g.id} gear={g} />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Empty State */}
                    {gear.length === 0 && !loading && (
                        <div className="text-center py-16">
                            <Package className="w-16 h-16 text-dark-700 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-white mb-2">No Gear Yet</h3>
                            <p className="text-dark-500">
                                Gear items will appear here once added to the database.
                            </p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default GearPage;
